import styles from 'signer-app/signature-modal/type/type.module.css';

import React from 'react';
import {
  defineMessages,
  FormattedMessage,
  IntlShape,
  useIntl,
} from 'react-intl';

import { Button } from '@dropbox/dig-components/buttons';
import { UIIcon } from '@dropbox/dig-icons';
import { SyncingLine } from '@dropbox/dig-icons/assets';
import { Chip } from '@dropbox/dig-components/chip';
import fonts from 'signer-app/signature-modal/constants/fonts';
import * as signatureTypes from 'signer-app/signature-modal/constants/signature-types';
import { useSignatureModalContext } from 'signer-app/signature-modal/contexts/signature-modal/context';
import type { SignatureModalContextShape } from 'signer-app/signature-modal/contexts/signature-modal/types';
import {
  changeFont,
  nextFont,
} from 'signer-app/signature-modal/contexts/signature-modal/actions';
import { SignatureModalLine } from 'signer-app/signature-modal/common/signature-modal-line';
import { AriaLiveProp, useAriaLive } from 'signer-app/utils/use-aria-live';
import ChipButtonsWrapper from '../common/chip-buttons-wrapper';

export interface TypeProps extends AriaLiveProp {
  intl: IntlShape;
  signatureModalContext: SignatureModalContextShape;
  enableInsertButtonCallback: (enableInsert: boolean) => void;
  initialTypeInValue: string;
  changeFont?: (fontIndex: number) => any;
  nextFont?: () => any;
  isNewSignerApp?: boolean;
}

const messages = defineMessages({
  typeInputAriaLabel: {
    id: '79acfb2fbcc082e632c1d72a179b39ea89036a3543d7e64e113e44c83ef4937e',
    description:
      'Screen reader label for an input field where the user types in their name.',
    defaultMessage: 'Enter your signature',
  },
  typeInputPlaceholder: {
    id: 'ffb250b6d34f1abebd00f43a9e046737114b8add002ee078769511eb5635b50d',
    description:
      'Placeholder text for an input field when the user types in their name.',
    defaultMessage: 'Your name',
  },
  fontChangedAnnouncement: {
    id: 'd2c49c0860f0634749e313343b9f01d1c61cd9aa931fdf451fccdb71c7deedb5',
    description:
      'Screen reader announcement when font is changed. The font change is applied on a signature that user types in.',
    defaultMessage: 'Font changed to {newFontName}',
  },
});

class Type extends React.PureComponent<TypeProps> {
  inputRef = React.createRef<HTMLInputElement>();

  state = {
    displayedFontIndex: 0,
  };

  componentDidMount() {
    const { dispatch } = this.props.signatureModalContext;

    // Always load the first font (index 0) when component mounts
    const changeFontFunc = this.props.changeFont ?? changeFont;
    dispatch(changeFontFunc(0));

    this.props.enableInsertButtonCallback(this.shouldEnableInsert());
  }

  onInputChange = () => {
    this.props.enableInsertButtonCallback(this.shouldEnableInsert());
  };

  onChangeFontPressed = () => {
    const { state, dispatch } = this.props.signatureModalContext;

    if (!state.isLoadingFont) {
      const nextFontFunc = this.props.nextFont ?? nextFont;
      dispatch(nextFontFunc());
    }
  };

  componentDidUpdate(prevProps: TypeProps) {
    const { signatureModalContext: prevContet } = prevProps;
    const { signatureModalContext: newContext, announce } = this.props;
    const { state: prevState } = prevContet;
    const { state: newState } = newContext;

    if (
      prevState.isLoadingFont === true &&
      newState.isLoadingFont === false &&
      prevState.currentFontIndex !== null &&
      newState.currentFontIndex !== null
    ) {
      this.setState({ displayedFontIndex: newState.currentFontIndex });

      announce(messages.fontChangedAnnouncement, {
        newFontName: fonts[newState.currentFontIndex],
      });
    }
  }

  getSignatureData() {
    const text = this.inputRef.current?.value.trim();

    return {
      create_type_code: signatureTypes.TYPED,
      signature: {
        text,
        font_family:
          fonts[this.props.signatureModalContext.state.currentFontIndex ?? 0],
      },
    };
  }

  shouldEnableInsert() {
    if (!this.inputRef.current) return false;

    return !!this.inputRef.current.value.trim().length;
  }

  isChineseLocale() {
    const intl = this.props.intl;
    const locale = intl ? intl.locale : null;
    return locale === 'zh-CN';
  }

  render() {
    const {
      intl,
      initialTypeInValue,
      signatureModalContext,
      isNewSignerApp = false,
    } = this.props;
    const { isLoadingFont } = signatureModalContext.state;
    const { displayedFontIndex } = this.state;

    return (
      <>
        <div className={styles.inputContainer}>
          <input
            ref={this.inputRef}
            onChange={this.onInputChange}
            className={styles.input}
            type="text"
            aria-label={intl.formatMessage(messages.typeInputAriaLabel)}
            placeholder={intl.formatMessage(messages.typeInputPlaceholder)}
            aria-required={true}
            defaultValue={initialTypeInValue}
            disabled={isLoadingFont}
            style={{ fontFamily: fonts[displayedFontIndex] }}
            data-qa-ref="signing-modal--type-in-field"
          />
          <SignatureModalLine />

          {!this.isChineseLocale() && isNewSignerApp ? (
            <ChipButtonsWrapper>
              <Chip
                data-testid="signing-modal--change-font"
                onClick={this.onChangeFontPressed}
                size="small"
              >
                <Chip.IconAccessory>
                  <UIIcon src={SyncingLine} />
                </Chip.IconAccessory>
                <Chip.Content>
                  <FormattedMessage
                    id="3b7a5782529215d8fc2ce31853460974ef47413f4c2fe71cf41038a50e1efb55"
                    description="Text for a button which changes the font of the typed-in signature when clicked."
                    defaultMessage="Change font"
                  />
                </Chip.Content>
              </Chip>
            </ChipButtonsWrapper>
          ) : null}
        </div>

        {!this.isChineseLocale() && !isNewSignerApp ? (
          <div className={styles.buttons}>
            <Button
              size="small"
              type="button"
              role="button"
              variant="borderless"
              withIconLeft={<UIIcon src={SyncingLine} />}
              className={styles.changeFontButton}
              data-qa-ref="signing-modal--change-font"
              data-testid="signing-modal--change-font"
              onClick={this.onChangeFontPressed}
            >
              <FormattedMessage
                id="3b7a5782529215d8fc2ce31853460974ef47413f4c2fe71cf41038a50e1efb55"
                description="Text for a button which changes the font of the typed-in signature when clicked."
                defaultMessage="Change font"
              />
            </Button>
          </div>
        ) : null}
      </>
    );
  }
}

export const TypeSignature = React.forwardRef(
  (
    props: Pick<
      TypeProps,
      'enableInsertButtonCallback' | 'initialTypeInValue' | 'isNewSignerApp'
    >,
    ref: React.ForwardedRef<any>,
  ) => {
    const intl = useIntl();
    const announce = useAriaLive();
    const signatureModalContext = useSignatureModalContext();

    return (
      <Type
        {...props}
        intl={intl}
        ref={ref}
        signatureModalContext={signatureModalContext}
        announce={announce}
      />
    );
  },
);
